import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faSpinner, faFlag } from '@fortawesome/free-solid-svg-icons';
import {
  priorityService,
  type Priority,
  type CreatePriorityPayload,
} from '@/services/priority.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

interface PriorityFormProps {
  initial?: Partial<CreatePriorityPayload>;
  onSubmit: (d: CreatePriorityPayload) => void;
  onCancel: () => void;
  saving: boolean;
}

function PriorityForm({ initial, onSubmit, onCancel, saving }: PriorityFormProps) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [colorHex, setColorHex] = useState(initial?.colorHex ?? '#6B7280');
  const [sortOrder, setSortOrder] = useState(String(initial?.sortOrder ?? 0));
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [isEmergency, setIsEmergency] = useState(initial?.isEmergency ?? false);
  const [responseTimeHours, setResponseTimeHours] = useState(
    initial?.responseTimeHours != null ? String(initial.responseTimeHours) : '',
  );
  const [completionTargetHours, setCompletionTargetHours] = useState(
    initial?.completionTargetHours != null ? String(initial.completionTargetHours) : '',
  );
  const [escalationThresholdPercent, setEscalationThresholdPercent] = useState(
    initial?.escalationThresholdPercent != null ? String(initial.escalationThresholdPercent) : '',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!code.trim()) errs.code = 'Code is required';
    if (!name.trim()) errs.name = 'Name is required';
    if (!/^#[0-9A-Fa-f]{6}$/.test(colorHex)) errs.colorHex = 'Must be a valid hex colour (#RRGGBB)';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      colorHex,
      sortOrder: parseInt(sortOrder, 10) || 0,
      isDefault,
      isEmergency,
      responseTimeHours: responseTimeHours ? parseInt(responseTimeHours, 10) : undefined,
      completionTargetHours: completionTargetHours ? parseInt(completionTargetHours, 10) : undefined,
      escalationThresholdPercent: escalationThresholdPercent
        ? parseInt(escalationThresholdPercent, 10)
        : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Code" htmlFor="pr-code" required error={errors.code}>
          <Input id="pr-code" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. P1, P2, EMRG" disabled={saving} maxLength={50} />
        </FormField>
        <FormField label="Sort Order" htmlFor="pr-sort">
          <Input id="pr-sort" type="number" min={0} value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)} disabled={saving} />
        </FormField>
      </div>
      <FormField label="Name" htmlFor="pr-name" required error={errors.name}>
        <Input id="pr-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Critical, High, Standard" disabled={saving} maxLength={200} />
      </FormField>
      <FormField label="Colour" htmlFor="pr-color" error={errors.colorHex}>
        <div className="flex items-center gap-3">
          <input type="color" id="pr-color-picker" value={colorHex}
            onChange={(e) => setColorHex(e.target.value)} disabled={saving}
            className="h-9 w-12 cursor-pointer rounded border border-border p-0.5" />
          <Input id="pr-color" value={colorHex} onChange={(e) => setColorHex(e.target.value)}
            placeholder="#6B7280" disabled={saving} maxLength={7} className="font-mono" />
        </div>
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Response Time (hours)" htmlFor="pr-response">
          <Input id="pr-response" type="number" min={0} value={responseTimeHours}
            onChange={(e) => setResponseTimeHours(e.target.value)} placeholder="e.g. 2" disabled={saving} />
        </FormField>
        <FormField label="Completion Target (hours)" htmlFor="pr-completion">
          <Input id="pr-completion" type="number" min={0} value={completionTargetHours}
            onChange={(e) => setCompletionTargetHours(e.target.value)} placeholder="e.g. 24" disabled={saving} />
        </FormField>
      </div>
      <FormField label="Escalation Threshold (%)" htmlFor="pr-escalation">
        <Input id="pr-escalation" type="number" min={0} max={100} value={escalationThresholdPercent}
          onChange={(e) => setEscalationThresholdPercent(e.target.value)} placeholder="e.g. 80" disabled={saving} />
      </FormField>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Flags</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Set as the default priority for new jobs
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isEmergency} onChange={(e) => setIsEmergency(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Emergency priority (triggers immediate escalation)
        </label>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function PrioritiesPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Priority | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['priorities', page, debouncedSearch],
    queryFn: () => priorityService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['priorities'] });

  const createMutation = useMutation({
    mutationFn: priorityService.create,
    onSuccess: () => { toast.success('Priority created'); setModalOpen(false); invalidate(); },
    onError: () => toast.error('Failed to create priority'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreatePriorityPayload> }) =>
      priorityService.update(id, payload),
    onSuccess: () => { toast.success('Priority updated'); setModalOpen(false); setEditTarget(null); invalidate(); },
    onError: () => toast.error('Failed to update priority'),
  });

  const deleteMutation = useMutation({
    mutationFn: priorityService.remove,
    onSuccess: () => { toast.success('Priority deleted'); invalidate(); },
    onError: () => toast.error('Failed to delete priority'),
  });

  const handleDelete = async (t: Priority) => {
    if (await confirmDelete(`${t.code} — ${t.name}`)) deleteMutation.mutate(t.id);
  };

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (t: Priority) => { setEditTarget(t); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Priorities"
        description="Urgency levels used across service jobs — each carries SLA targets and escalation rules"
        onAdd={can('priority:create') ? openCreate : undefined}
        addLabel="Add Priority"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search by code or name..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Priority</th>
                  <th className="px-4 py-3 font-medium text-foreground">Response</th>
                  <th className="px-4 py-3 font-medium text-foreground">Completion</th>
                  <th className="px-4 py-3 font-medium text-foreground">Flags</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.colorHex }} />
                        <span className="font-mono font-medium text-foreground">{t.code}</span>
                        <span className="text-muted-foreground">{t.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {t.responseTimeHours != null ? `${t.responseTimeHours}h` : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {t.completionTargetHours != null ? `${t.completionTargetHours}h` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {t.isDefault && (
                          <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary">Default</span>
                        )}
                        {t.isEmergency && (
                          <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">Emergency</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${t.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('priority:update') && (
                          <button onClick={() => openEdit(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${t.name}`}>
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('priority:delete') && (
                          <button onClick={() => handleDelete(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${t.name}`} disabled={deleteMutation.isPending}>
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faFlag}
          title={search ? 'No priorities match your search' : 'No priorities yet'}
          description={search ? 'Try a different search term.' : 'Add priority levels such as Critical, High, Standard, and Low with SLA targets.'}
          action={!search && can('priority:create') ? <Button onClick={openCreate}>Add Priority</Button> : undefined}
        />
      )}

      <Modal open={modalOpen} onClose={closeModal} title={editTarget ? 'Edit Priority' : 'Add Priority'} size="md">
        <PriorityForm
          initial={editTarget ? {
            code: editTarget.code, name: editTarget.name, colorHex: editTarget.colorHex,
            sortOrder: editTarget.sortOrder, isDefault: editTarget.isDefault, isEmergency: editTarget.isEmergency,
            responseTimeHours: editTarget.responseTimeHours ?? undefined,
            completionTargetHours: editTarget.completionTargetHours ?? undefined,
            escalationThresholdPercent: editTarget.escalationThresholdPercent ?? undefined,
          } : undefined}
          onSubmit={(p) => editTarget ? updateMutation.mutate({ id: editTarget.id, payload: p }) : createMutation.mutate(p)}
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
